import { useMemo } from "react";
import { useSelector } from "react-redux";

import useCustomerLanguage from "web/hooks/useCustomerLanguage";
import useSubscriptionsView from "web/hooks/useSubscriptionsView";

import type { ICategory } from "web/types/Category";
import type { ICategoryCount } from "web/types/CategoryCount";

import { useGetMenuWebviewQuery } from "web/features/api/graphQLApiSlice";
import { useGetEmployeeDetailsQuery } from "web/features/employee/employeeApiSlice";

import buildCategoryListData from "./buildCategoryListData";
import findStartCategoryData from "./findStartCategoryData";
import getUniqueTreeCategories from "./getUniqueTreeCategories";
import processCategoryTree from "./processCategoryTree";
import processMenuWebviewData from "./processMenuWebviewData";
import { MenuItemContext } from "./types";

interface IUseNavigationArgs {
  categoriesTree: string[] | undefined;
  categoriesData: ICategory[];
  categoriesSizes: ICategoryCount[];
}

const useNavigationData = ({
  categoriesTree,
  categoriesData,
  categoriesSizes,
}: IUseNavigationArgs) => {
  const {
    app: { storeConfig },
  } = useSelector((state) => state);
  const { hasEnabledAnySubscriptions, config: subscriptionConfig } =
    useSelector((state) => state.subscriptionQuote);

  const { navigationCategoryId: startCategoryId = "" } = useSelector(
    (state) => state.app
  );

  const { data: employee } = useGetEmployeeDetailsQuery();
  const {
    functionalities,
    msiCartsEnabled: msiCartsEnabledSelector,
    mbActiveEnabled: mbActiveEnabledSelector,
  } = employee || {};

  const {
    mybenefit_active_url: myBenefitActiveUrlPl,
    mybenefit_active_url_en: myBenefitActiveUrlEn,
    mybenefit_active_menu: myBenefitActiveMenuPl,
    mybenefit_active_menu_en: myBenefitActiveMenuEn,
    msi_url: msiUrlPl,
    msi_url_en: msiUrlEn,
    msi_menu_en: msiMenuEn,
  } = storeConfig;

  const { isEn } = useCustomerLanguage();
  const myBenefitActiveUrl = isEn ? myBenefitActiveUrlEn : myBenefitActiveUrlPl;
  const myBenefitActiveMenu = isEn
    ? myBenefitActiveMenuEn
    : myBenefitActiveMenuPl;

  const msiUrl = isEn ? msiUrlEn : msiUrlPl;

  const {
    data: menuDataResponse,
    isLoading,
    isError,
  } = useGetMenuWebviewQuery();
  const menuData = menuDataResponse?.menu;

  const { enableMobilePreview, enableNewSubscriptionUI } =
    useSubscriptionsView();

  const categoryTreeData = useMemo(() => {
    const treeCurrent = processCategoryTree(
      categoriesTree,
      categoriesData,
      startCategoryId as number
    );
    const treeCurrentUnique = getUniqueTreeCategories(treeCurrent);
    const startCategoryData = findStartCategoryData(
      categoriesData,
      startCategoryId as number
    );

    return {
      treeCurrent,
      treeCurrentUnique,
      startCategoryData,
    };
  }, [categoriesTree, categoriesData, startCategoryId]);

  const categoryListData = useMemo(() => {
    return buildCategoryListData(
      categoryTreeData.treeCurrentUnique,
      categoriesTree,
      categoriesSizes
    );
  }, [categoryTreeData.treeCurrentUnique, categoriesTree, categoriesSizes]);

  const menuContext: MenuItemContext = useMemo(
    () => ({
      isEn,
      functionalities,
      mbActiveEnabledSelector,
      myBenefitActiveUrl,
      myBenefitActiveMenu,
      msiUrl,
      msiMenuEn,
      msiCartsEnabledSelector,
      hasEnabledAnySubscriptions,
      subscriptionConfig,
      enableMobilePreview: enableMobilePreview || false,
      enableNewSubscriptionUI: enableNewSubscriptionUI || false,
    }),
    [
      isEn,
      functionalities,
      mbActiveEnabledSelector,
      myBenefitActiveUrl,
      myBenefitActiveMenu,
      msiUrl,
      msiMenuEn,
      msiCartsEnabledSelector,
      hasEnabledAnySubscriptions,
      subscriptionConfig,
      enableMobilePreview,
      enableNewSubscriptionUI,
    ]
  );

  const processedMenuItems = useMemo(() => {
    return processMenuWebviewData(
      menuData,
      menuContext,
      categoriesData,
      categoriesTree,
      categoriesSizes
    );
  }, [menuData, menuContext, categoriesData, categoriesTree, categoriesSizes]);

  return {
    ...categoryTreeData,
    categoryListData,
    processedMenuItems,
    isLoading,
    isError,
  };
};

export default useNavigationData;
